import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Paper, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Print } from "@material-ui/icons";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import swal from "sweetalert2";
import { b64toBlob } from "../../blocks/utility";
import { traducirLabel, traducirMessage } from "../../blocks/traductor";
import { IGuiaConsultaDto } from "../../entities/guias/IGuiaConsultaDto";
import useGuiaPdf, { GUIA_PDF_IMPRESION_REIMPRESION } from "../../hooks/useGuiaPdf";
import useGuiaEtiquetasPdf from "../../hooks/useGuiaEtiquetasPdf";
import { ApplicationState } from "../../redux/reducers";
import { clearGuiaConsulta } from "../../redux/actions/guiaActions";
import LoadingDialog from "../controls/LoadingDialog";
import HeaderGuia from "../header/HeaderGuia";
import GuiaConsultaResumenView from "../resumen/GuiaConsultaResumenView";

const PORTAL_SUB_TAB_KEY = "portalSubTabCotizacion";

const useStyles = makeStyles(() =>
    createStyles({
        mainTitle: {
            color: "#19754e",
            fontSize: "18px",
            fontWeight: "bold",
            textAlign: "center"
        },
        guiaNumero: {
            color: "#000",
            textDecoration: "underline",
            fontWeight: 700,
            fontSize: "22px",
            textAlign: "center",
            marginTop: 8,
            marginBottom: 16
        },
        actions: {
            display: "flex",
            justifyContent: "center",
            gap: 16,
            marginBottom: 16,
            flexWrap: "wrap"
        }
    })
);

interface StateProps {
    consulta: IGuiaConsultaDto | null;
}

interface DispatchProps {
    clearGuiaConsulta: typeof clearGuiaConsulta;
}

type Props = StateProps & DispatchProps;

const BodyGuiaConsulta = ({ consulta, clearGuiaConsulta }: Props) => {
    const classes = useStyles();
    const history = useHistory();
    const { obtenerPdfBase64 } = useGuiaPdf();
    const { obtenerEtiquetasPdfBase64 } = useGuiaEtiquetasPdf();
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [loadingEtiquetas, setLoadingEtiquetas] = useState(false);
    const [etiquetasImpresasEnSesion, setEtiquetasImpresasEnSesion] = useState(false);

    const etiquetasYaImpresas =
        etiquetasImpresasEnSesion ||
        (consulta != null && consulta.fechaImpresionEtiqueta != null);

    useEffect(() => {
        const esGuiasPortalExterno = localStorage.getItem("verGuiasPortalExterno");
        if (esGuiasPortalExterno !== "true") {
            history.replace("/");
            return;
        }
        if (!consulta) {
            localStorage.setItem(PORTAL_SUB_TAB_KEY, "Guias");
            history.replace("/");
        }
    }, [consulta, history]);

    const handleRegreso = () => {
        clearGuiaConsulta();
        localStorage.setItem(PORTAL_SUB_TAB_KEY, "Guias");
        history.push("/");
    };

    const descargarPdfDesdeBase64 = (base64: string, nombreArchivo: string) => {
        const blob = b64toBlob(base64, "application/pdf");
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = nombreArchivo;
        link.click();
        URL.revokeObjectURL(blobUrl);
    };

    const handlePdf = async () => {
        if (!consulta || loadingPdf || loadingEtiquetas) {
            return;
        }
        setLoadingPdf(true);
        try {
            const response = await obtenerPdfBase64(consulta.guiaId, GUIA_PDF_IMPRESION_REIMPRESION);
            descargarPdfDesdeBase64(response, `Guia-${consulta.numGuia}.pdf`);
        } catch {
            swal.fire({
                titleText: traducirMessage(276),
                text: traducirMessage(215),
                type: "error"
            });
        } finally {
            setLoadingPdf(false);
        }
    };

    const handleEtiquetas = async () => {
        if (!consulta || loadingPdf || loadingEtiquetas || etiquetasYaImpresas) {
            return;
        }
        setLoadingEtiquetas(true);
        try {
            const response = await obtenerEtiquetasPdfBase64(consulta.guiaId);
            descargarPdfDesdeBase64(response, `GuiaEtiquetas-${consulta.numGuia}.pdf`);
            setEtiquetasImpresasEnSesion(true);
        } catch {
            swal.fire({
                titleText: traducirMessage(276),
                text: traducirMessage(215),
                type: "error"
            });
        } finally {
            setLoadingEtiquetas(false);
        }
    };

    if (!consulta) {
        return null;
    }

    return (
        <main>
            <HeaderGuia ocultarStepper onVolver={handleRegreso} />
            <div style={{ width: "90%", margin: "auto", marginTop: "2em" }}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Paper elevation={3}>
                            <Box px={3} pt={3} pb={1}>
                                <Typography className={classes.mainTitle}>
                                    {traducirLabel(354)}
                                </Typography>
                                <Typography className={classes.guiaNumero} component="div">
                                    {consulta.numGuia}
                                </Typography>
                                <Box className={classes.actions}>
                                    <Button
                                        color="primary"
                                        onClick={handlePdf}
                                        disabled={loadingPdf || loadingEtiquetas}
                                        startIcon={<Print />}
                                    >
                                        {traducirLabel(164)}
                                    </Button>
                                    <Button
                                        color="primary"
                                        onClick={handleEtiquetas}
                                        disabled={loadingPdf || loadingEtiquetas || etiquetasYaImpresas}
                                        startIcon={<Print />}
                                    >
                                        {traducirLabel(162)}
                                    </Button>
                                </Box>
                            </Box>
                            <GuiaConsultaResumenView consulta={consulta} />
                        </Paper>
                    </Grid>
                </Grid>
            </div>
            {(loadingPdf || loadingEtiquetas) && (
                <LoadingDialog titulo={traducirLabel(240)} abierto={true} />
            )}
        </main>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    consulta: state.guiaConsulta
});

const mapDispatchToProps = {
    clearGuiaConsulta
};

export default connect(mapStateToProps, mapDispatchToProps)(BodyGuiaConsulta);
